import { API_URL } from "./constants";

/**
 * Maps a generic endpoint to a provider-specific one.
 * For Azure (default), endpoints are unchanged.
 * For GCP, endpoints are prefixed with "gcp/".
 *
 * @param {string} provider - "azure" or "gcp"
 * @param {string} endpoint - The endpoint path (without API_URL prefix)
 * @returns {string} The full API URL for the given provider
 */
export const getProviderApiUrl = (provider, endpoint) => {
  if (provider === "gcp") {
    return `${API_URL}gcp/${endpoint}`;
  }
  return `${API_URL}${endpoint}`;
};

/**
 * Provider-specific endpoint mapping.
 * Maps Azure endpoints to their GCP equivalents.
 */
export const PROVIDER_ENDPOINTS = {
  azure: {
    "get-all-data": "get-all-data",
    "previous-monthly-billing": "previous-monthly-billing",
    "billing-forecast": "billing-forecast",
    subscriptions: "subscriptions",
    "storage-accounts": "storage-accounts",
    databases: "databases",
    "function-apps": "function-apps",
    "cdn-data": "cdn-data",
    "alert-rules": "alert-rules",
    "active-alerts": "active-alerts",
    "previous-alerts": "previous-alerts",
    "container-logs": "container-logs",
    "metrics-logs": "metrics-logs",
    "static-apps": "static-apps",
    "compute-instances": "list-azure-vms",
    "cloud-run": "container-apps",
    secrets: "key-vault",
    "load-balancers": "load-balancers",
  },
  gcp: {
    "get-all-data": "gcp/get-all-data",
    "previous-monthly-billing": "gcp/billing",
    "billing-forecast": "gcp/billing-forecast",
    subscriptions: "gcp/projects",
    "storage-accounts": "gcp/storage-buckets",
    databases: "gcp/cloud-sql",
    "function-apps": "gcp/cloud-functions",
    "cdn-data": "gcp/cdn",
    "alert-rules": "gcp/alert-policies",
    "active-alerts": "gcp/alert-policies",
    "previous-alerts": "gcp/db-previous-alerts",
    "container-logs": "gcp/console-logs",
    "metrics-logs": "gcp/compute-metrics",
    "static-apps": "gcp/storage-buckets",
    "compute-instances": "gcp/compute-instances",
    "cloud-run": "gcp/cloud-run",
    secrets: "gcp/secrets",
    "load-balancers": "gcp/load-balancers",
  },
};

/**
 * Gets the correct API URL for a given provider and logical endpoint.
 *
 * @param {string} provider - "azure" or "gcp"
 * @param {string} logicalEndpoint - The logical endpoint name (e.g., "subscriptions")
 * @returns {string} The full API URL
 */
export const getEndpointUrl = (provider, logicalEndpoint) => {
  const endpoints = PROVIDER_ENDPOINTS[provider] || PROVIDER_ENDPOINTS.azure;
  const endpoint = endpoints[logicalEndpoint] || logicalEndpoint;
  return `${API_URL}${endpoint}`;
};

/**
 * Provider display labels mapping.
 * Maps generic labels to provider-specific ones.
 */
export const PROVIDER_LABELS = {
  azure: {
    subscriptions: "Subscriptions",
    storageAccounts: "Storage Accounts",
    storageContainer: "Storage Container",
    databases: "Databases",
    sites: "Sites",
    functionApp: "Function App",
    cdn: "CDN",
    alertRules: "Alert Rules",
    activeAlert: "Active Alert",
    previousAlerts: "Previous Alerts",
    consoleLogs: "Console Logs",
    metricsLogs: "Metrics Logs",
    billings: "Billings",
    billingForecast: "Billing Forecast",
    computeEngine: "Virtual Machines",
    cloudRun: "Container Apps",
    secretManager: "Key Vault",
    loadBalancer: "Load Balancers",
  },
  gcp: {
    subscriptions: "Projects",
    storageAccounts: "Storage Buckets",
    storageContainer: "Bucket Objects",
    databases: "Cloud SQL",
    sites: "Static Hosting",
    functionApp: "Cloud Functions",
    cdn: "Cloud CDN",
    alertRules: "Alert Policies",
    activeAlert: "Active Incidents",
    previousAlerts: "Previous Alerts",
    consoleLogs: "Cloud Logging",
    metricsLogs: "Cloud Monitoring",
    billings: "Billings",
    billingForecast: "Billing Forecast",
    computeEngine: "Compute Engine",
    cloudRun: "Cloud Run",
    secretManager: "Secret Manager",
    loadBalancer: "Load Balancers",
  },
};

/**
 * Gets the display label for a given provider and item.
 *
 * @param {string} provider - "azure" or "gcp"
 * @param {string} labelKey - The label key
 * @returns {string} The display label
 */
export const getProviderLabel = (provider, labelKey) => {
  const labels = PROVIDER_LABELS[provider] || PROVIDER_LABELS.azure;
  return labels[labelKey] || labelKey;
};
